'use client'
import { AppText } from '@otomoro/components/shared/AppText'
import { SanityImage } from '@otomoro/components/shared/SanityImage'
import { PortableText, PortableTextComponents, SanityImageAssetDocument } from 'next-sanity'
import { PortableTextObject } from 'sanity'
import { propertiesClasses, PropertiesVariants } from './Properties.css'
import clsx from 'clsx'
import { useRef, useState } from 'react'
import { useInView, motion, AnimatePresence } from 'framer-motion'
import gsap from 'gsap'
import ScrollTrigger from 'gsap/dist/ScrollTrigger'
gsap.registerPlugin(ScrollTrigger)
import { useGSAP } from '@gsap/react'
import { AppList } from '../AppList'
import { DownloadInfoProps } from '../Button/Button'
import { DownloadEmail } from '../DownloadEmail/DownloadEmail'
import { SectionHeader } from '../SectionHeader'

interface PropertiesProps extends PropertiesVariants {
	_key: string
	tag?: string
	title: PortableTextObject
	description?: PortableTextObject
	properties: any[]
	display3d: boolean
	model3d: any
	illustration: SanityImageAssetDocument
	downloadInfo: DownloadInfoProps
}

export const Properties: React.FC<PropertiesProps> = ({
	_key,
	tag,
	title,
	description,
	properties,
	display3d,
	model3d,
	illustration,
	downloadInfo
}) => {
	const {
		base,
		wrapper,
		propertiesGrid,
		firstProperties,
		lastProperties,
		propertyBox,
		image,
		figure,
		model,
		gridWrapper,
		titleStyle,
		descStyle,
	} = propertiesClasses()

	const modelViewer = useRef(null)
	const propsRef = useRef(null)
  const isInView = useInView(modelViewer)
	const [last, setLast] = useState(false)

	useGSAP(() => {
		let mm = gsap.matchMedia()

		mm.add("(min-width: 1024px)", () => {
			gsap.to(propsRef.current, {
				scrollTrigger: {
					trigger: propsRef.current,
					start: 'top 20%',
					end: 'bottom 80%',
					scrub: 0.9,
					onUpdate: ({ progress }) => {
						progress >= 0.4 ? setLast(true) : setLast(false)
					},
				},
			})
		})
	})

	const descComponents = {
		block: {
			normal: ({ children }: any) => (
				<AppText as="p" size="medium" color="white" className={descStyle()}>
					{children}
				</AppText>
			),
		},
		marks: {
			highlight: ({ children }: any) => <span className="text-mainPink">{children}</span>,
		},
	} as PortableTextComponents

	return (
		<section className={base()} ref={propsRef}>
			<div className={wrapper()}>
				<SectionHeader tag={tag} title={title} noPx theme='dark' className={titleStyle()} />
				{description && <PortableText value={description} components={descComponents} />}
				<div className={gridWrapper()}>
					<div className={propertiesGrid()}>
						<AppList noWrapper className={clsx(firstProperties(), 'lg:hidden')}>
							{properties &&
								properties.map((property, index) => {
									return <li key={index} className={propertyBox()}>
										<AppText
											as="h3"
											size="h3"
											weight="bold"
											color="white"
											textTransform='uppercase'
											family='condensed'
										>
											{property.name}
										</AppText>
										<AppText as="p" size="base" className='mt-[0.5rem]' color="white">
											{property.value}
										</AppText>
									</li>
							})}
						</AppList>
						<div className='hidden lg:block'>
							<AnimatePresence mode='wait' initial={false}>
								{!last && (
									<motion.ul
										initial={{ opacity: 1, height: 0 }}
										animate={{ opacity: 1, height: 'auto' }}
										exit={{
											opacity: 0,
											height: 0,
											transition: { duration: 0.25 },
										}}
										transition={{ delay: 0.25 }}
										className={firstProperties()}
									>
										{properties &&
											properties.map((property, index) => {
												if (properties.length > 5 && index >= 5) return

												return <li key={index} className={propertyBox()}>
													<AppText
														as="h3"
														size="h3"
														weight="bold"
														color="white"
														textTransform='uppercase'
														family='condensed'
													>
														{property.name}
													</AppText>
													<AppText as="p" size="base" className='mt-[0.5rem] lg:mt-0 lg:shrink-0' color="white">
														{property.value}
													</AppText>
												</li>
										})}
									</motion.ul>
								)}
							</AnimatePresence>
							<AnimatePresence mode='wait'>
								{last && (
									<motion.ul
										initial={{ opacity: 0, height: 0 }}
										animate={{ opacity: 1, height: 'auto' }}
										exit={{
											opacity: 0,
											height: 0,
											transition: { duration: 0.25 },
										}}
										transition={{ delay: 0.25 }}
										className={clsx(lastProperties())}
									>
										{properties &&
											properties.map((property, index) => {
												if (properties.length > 5 && index < 5) return

												return <li key={index} className={propertyBox()}>
													<AppText
														as="h3"
														size="h3"
														weight="bold"
														color="white"
														textTransform='uppercase'
														family='condensed'
													>
														{property.name}
													</AppText>
													<AppText as="p" size="small" className='mt-[0.5rem] lg:mt-0 lg:shrink-0' color="white">
														{property.value}
													</AppText>
												</li>
										})}
									</motion.ul>
								)}
							</AnimatePresence>
						</div>
					</div>
				</div>
				{downloadInfo?.title && (
					<span className='hidden lg:block col-span-2 row-start-5 row-end-5'>
						<DownloadEmail _key={_key} infos={downloadInfo} theme='dark' className='!mt-4' noPx/>
					</span>
				)}
				{(display3d && model3d) && (
					<div className={clsx(model(), 'h-[50vh] lg:h-[50rem]')}>
						<model-viewer
							className="size-full"
							src={model3d.asset.url}
							shadowIntensity={1}
							alt="A 3D model of the product"
							ref={modelViewer}
							camera-controls
							disable-zoom
							camera-orbit={isInView && "calc(15deg - env(window-scroll-y) * 360deg) 75deg auto"}
						>
						</model-viewer>
					</div>
				)}

				{((!display3d || !model3d) && illustration?.image) && (
					<SanityImage
						figureClass={figure()}
						className={image()}
						image={illustration.image}
						alt=""
						width={1000}
						height={1000}
						sizes={`100vw`}
					/>
				)}
			</div>
			{downloadInfo?.title && (
				<span className='lg:hidden'>
					<DownloadEmail _key={_key} infos={downloadInfo} theme='dark' className='!mt-4' noPx/>
				</span>
			)}
		</section>
	)
}
